import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { createFocusTrap } from 'focus-trap'
import {
	hamburgerButton,
	menuPrimary,
	focusTrapMenu,
	menuParentItems,
	mqMenu,
	mqProject,
	projectTabs,
	projectPanels,
	tabWrapper,
	viewport,
	heroEl,
	projectTitle,
	projectData,
	projectCards,
	projectFilters,
	header,
	headerLogo,
	projectNavigationProgramaMenu,
	projectNavigationLayoutMenu,
	footerSocialWrapper,
	footerContactButton,
	floatingMenu,
	floatingMenuToggle,
	homeContactMenu,
	homeContactButtons,
	focusTrapContactMenu,
	sortTableButtons,
	mediaTextBlocks,
} from './elements.js'
import { expandSection, collapseSection } from './lib.js'

const projectDataOffset = 48
let currentTrap

/**
 * Toggle main hamburger menu
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2021.1
 */
export function toggleMenu(e) {
	const expanded =
		hamburgerButton.getAttribute('aria-expanded') === 'true' || false
	const menuState = menuPrimary.getAttribute('data-visible') === 'true' || false

	if (e.currentTarget === hamburgerButton || (e.keyCode === 27 && expanded)) {
		menuPrimary.setAttribute('data-visible', !menuState)
		hamburgerButton.setAttribute('aria-expanded', !expanded)

		if (expanded) {
			enableBodyScroll(menuPrimary)
			focusTrapMenu.deactivate()
			collapseAllSubmenus()
		} else {
			disableBodyScroll(menuPrimary)
			setTimeout(() => {
				focusTrapMenu.activate()
			}, 150)
		}
	}
}

/**
 * Collapse all submenus from primary navigation
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2020.0
 * @param       int transitionTiming
 */
function collapseAllSubmenus() {
	menuParentItems.forEach((item) => {
		if (item.getAttribute('aria-expanded') === 'true') {
			item.setAttribute('aria-expanded', false)
			item.nextElementSibling.setAttribute('data-collapsed', true)
		}
	})
}

/**
 * Handle main menu clicks to show submenus
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export function handleMenuClick(e) {
	const isOutside = !e.target.closest('#js-menu-primary')
	if (!e.keyCode) {
		if (isOutside) {
			collapseAllSubmenus()
		} else if (e.target.hasAttribute('aria-expanded')) {
			e.preventDefault()
			const menuItem = e.target.closest('[aria-expanded]')
			const isExpanded =
				menuItem.getAttribute('aria-expanded') === 'true' || false
			const subMenu = menuItem.nextElementSibling

			if (!mqMenu.matches) {
				if (isExpanded) {
					subMenu.setAttribute('data-collapsed', true)
				} else {
					menuParentItems.forEach((item) => {
						if (
							item !== menuItem &&
							item.getAttribute('aria-expanded') === 'true'
						) {
							item.setAttribute('aria-expanded', false)
							item.nextElementSibling.setAttribute('data-collapsed', true)
						} else {
							subMenu.setAttribute('data-collapsed', false)
						}
					})
				}
			} else if (!isExpanded) {
				collapseAllSubmenus()
			}
			menuItem.setAttribute('aria-expanded', !isExpanded)
			subMenu.setAttribute('data-collapsed', isExpanded)
		}
	} else if (e.keyCode === 27) {
		collapseAllSubmenus()
	}
}

/**
 * Handle project tab clicks to show/hide panels
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export function handleTabsClick(e) {
	e.preventDefault()
	const currentTab = tabWrapper.querySelector('[aria-selected="true"]')

	if (e.currentTarget !== currentTab) {
		switchTab(currentTab, e.currentTarget)
	}
}

/**
 * Handle project tab keyboard navigation to show/hide panels
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export function handleTabsKeyboard(e) {
	const index = Array.prototype.indexOf.call(projectTabs, e.currentTarget)

	// Work out which key the user is pressing and calculate the new tab's index where
	const dir =
		e.which === 37
			? index - 1
			: e.which === 39
			? index + 1
			: e.which === 40
			? 'down'
			: null

	if (dir !== null) {
		e.preventDefault()
		// If the down key is pressed, move focus to the open panel, otherwise switch to the adjacent tab
		dir === 'down'
			? projectPanels[index].focus()
			: projectTabs[dir]
			? switchTab(e.currentTarget, projectTabs[dir])
			: void 0
	}
}

/**
 * Switch between tabs
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
function switchTab(oldTab, newTab) {
	newTab.focus()
	// Make the active tab focusable by the user (Tab key)
	newTab.removeAttribute('tabindex')
	// Set the selected state
	newTab.setAttribute('aria-selected', 'true')
	oldTab.removeAttribute('aria-selected')
	oldTab.setAttribute('tabindex', '-1')
	// Get the indices of the new and old tabs to find the correct tab panels to show and hide
	const index = Array.prototype.indexOf.call(projectTabs, newTab)
	const oldIndex = Array.prototype.indexOf.call(projectTabs, oldTab)
	projectPanels[oldIndex].setAttribute('aria-hidden', 'true')
	projectPanels[index].setAttribute('aria-hidden', 'false')
	if (mqProject.matches) {
		const currentPanel = document.getElementById(projectPanels[index].id)
		window.scrollTo({
			top: currentPanel.offsetTop - 154,
			behavior: 'smooth',
		})
	}
}

/**
 * Make element sticky only if there is enough vertical space for it
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export function makeSticky(el) {
	if (el) {
		let offset
		if (el.classList.contains('js-sticky-title')) {
			offset = projectTitle.clientHeight + projectDataOffset
		} else {
			offset = el.getAttribute('data-offset')
				? parseInt(el.getAttribute('data-offset'))
				: 0
		}
		if (el.clientHeight + offset < viewport.clientHeight) {
			el.classList.add('u-sticky')
			el.classList.remove('u-non-sticky')
		} else {
			el.classList.remove('u-sticky')
			el.classList.add('u-non-sticky')
		}
	}
}

/**
 * Animate hero opacity and transform
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export function heroAnimation() {
	// Set the animation endpoint
	const animatedHeight = heroEl.clientHeight / 2
	const scroll = window.scrollY
	// Get animation values
	const opacity = scroll > animatedHeight ? 0 : 1 - scroll / animatedHeight
	const transform = scroll > animatedHeight ? animatedHeight / 3 : scroll / 3
	// Set custom properties
	heroEl.style.setProperty('--hero-opacity', opacity)
	heroEl.style.setProperty('--hero-transform', `translateY(-${transform}px)`)
}

/**
 * Apply animation to hero elements on scroll
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export const handleHero = (entries) => {
	if (entries[0].isIntersecting) {
		window.addEventListener('scroll', heroAnimation)
		hamburgerButton.classList.remove('c-hamburger--default')
		header.classList.remove('c-header--fixed')
		headerLogo.classList.remove('c-logo--swap')
		headerLogo.classList.add('c-logo--light')
	} else {
		// Remove the event listener for better performance
		window.removeEventListener('scroll', heroAnimation)
		hamburgerButton.classList.add('c-hamburger--default')
		header.classList.add('c-header--fixed')
		headerLogo.classList.add('c-logo--swap')
		headerLogo.classList.remove('c-logo--light')
	}
}

/**
 * Observe hero element and apply animation only if visible
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export const heroObserver = new window.IntersectionObserver(handleHero, {
	rootMargin: '-64px',
	// threshold: 0.5,
})

/**
 * Play/Pause videos on click
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export function playPauseVideo(e) {
	const video = e.currentTarget

	if (video.paused) {
		video.play()
	} else {
		video.pause()
	}
}

/**
 * Adjust element height to 100% viewport height
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export function adjustHeight(e) {
	const viewportHeight = `${window.innerHeight}px`
	e.style.setProperty('--vh', viewportHeight)
}

/**
 * Make element sticky only if there is enough vertical space for it
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export function setStickyOffset() {
	const offset = `${projectTitle.clientHeight + projectDataOffset}px`
	projectData.style.setProperty('--top', offset)
}

/**
 * Highlight projects for the selected category
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2023.1
 */
export function manageProjectFilters(e) {
	let selection
	// Check if e is undefined
	if (e) {
		selection = e.currentTarget
	} else {
		const urlParams = new URLSearchParams(window.location.search)
		const filter = urlParams.get('filter')

		if (filter) {
			selection = projectNavigationProgramaMenu.querySelector(
				`[data-value="${filter}"]`
			)

			if (!selection) {
				return
			}
		} else {
			return
		}
	}
	const menu = selection.closest('ul')
	const menuButton = menu.previousElementSibling

	const menuOptions = menu.querySelectorAll('.js-project-filter-button')

	// Set button menu attributes
	menuOptions.forEach((item) => {
		item.removeAttribute('aria-selected')
	})
	selection.setAttribute('aria-selected', 'true')

	updatePageFilterState(selection.getAttribute('data-value'))

	// Retrieve current filters
	const programa = projectNavigationProgramaMenu
		.querySelector('[aria-selected="true"]')
		.getAttribute('data-value')

	// Set project menu attributes
	projectCards.forEach((item) => {
		item.setAttribute('data-remove-highlight', 'false')
	})

	// Close menu
	menu.setAttribute('data-visible', 'false')

	if (menuButton) {
		menuButton.setAttribute('aria-expanded', 'false')

		// Update button label
		menuButton.querySelector('span').textContent = selection.textContent
	}

	// Filter out by programa
	const disabledByPrograma = [...projectCards].filter((element) => {
		const elProgramas = element.getAttribute('data-programa')

		if (programa !== 'all' && !elProgramas.includes(programa)) {
			return true
		}

		return false
	})

	disabledByPrograma.forEach((item) => {
		item.setAttribute('data-remove-highlight', 'true')
	})
}

/**
 * Highlight projects for the selected category
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2023.1
 */
export function manageProjectsLayout(e) {
	const selection = e.currentTarget
	const menu = selection.closest('ul')
	const menuButton = menu.previousElementSibling

	const menuOptions = menu.querySelectorAll('.js-projects-layout-button')

	// Set button menu attributes
	menuOptions.forEach((item) => {
		item.removeAttribute('aria-selected')
	})
	selection.setAttribute('aria-selected', 'true')

	// Retrieve current filters
	const layout = projectNavigationLayoutMenu
		.querySelector('[aria-selected="true"]')
		.getAttribute('data-value')

	// Close menu
	menu.setAttribute('data-visible', 'false')
	if (menuButton) {
		menuButton.setAttribute('aria-expanded', 'false')
		menuButton.querySelector('span').textContent = selection.textContent
	}

	const container = document.querySelector('.js-layout')

	if (container) {
		container.setAttribute('data-layout', layout)
		// Save session storage for future visits
		sessionStorage.setItem('layout', layout)
	}
}

/**
 * Toggle project navigation
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2022.15
 */
export function toggleProjectNavigation(e) {
	const button = e.currentTarget
	const menu = button.nextElementSibling

	const expanded = button.getAttribute('aria-expanded') === 'true' || false
	const menuState = menu.getAttribute('data-visible') === 'true' || false

	menu.setAttribute('data-visible', !menuState)
	button.setAttribute('aria-expanded', !expanded)
}

/**
 * Toggle contact dialog
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2022.15
 */
export function handleContactDialog(e) {
	const targetDialog = e.currentTarget.getAttribute('data-target')

	if (!targetDialog) {
		return
	}

	const dialog = document.getElementById(targetDialog)
	const focusTrapDialog = createFocusTrap(dialog, {
		clickOutsideDeactivates: true,
		preventScroll: true,
	})

	if (targetDialog === 'close') {
		currentTrap.deactivate()
		e.currentTarget.closest('dialog').close()
		return
	}

	dialog.showModal()
	focusTrapDialog.activate()
	currentTrap = focusTrapDialog
}

/**
 * Move footer contact button inside Social Networks list
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2022.15
 */
export function moveFooterContactButton() {
	const newListItem = document.createElement('li')
	newListItem.classList.add('c-social')
	newListItem.appendChild(footerContactButton)

	footerSocialWrapper.appendChild(newListItem)
}

/**
 * Toggle floating menu
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2023.1
 */
export function toggleFloatingMenu(e) {
	const expanded =
		floatingMenuToggle.getAttribute('aria-expanded') === 'true' || false
	const menuState =
		floatingMenu.getAttribute('data-visible') === 'true' || false
	if (
		e.currentTarget === floatingMenuToggle ||
		(e.keyCode === 27 && expanded)
	) {
		floatingMenu.setAttribute('data-visible', !menuState)
		floatingMenuToggle.setAttribute('aria-expanded', !expanded)
	}
}

/**
 * Handle toggle of contact home menu
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2023.1
 */
export function toggleHomeContactMenu(e) {
	if (e.type === 'click') {
		const expanded =
			e.currentTarget.getAttribute('aria-expanded') === 'true' || false
		const menuState =
			homeContactMenu.getAttribute('data-visible') === 'true' || false
		homeContactMenu.setAttribute('data-visible', !menuState)

		homeContactButtons.forEach((item) => {
			item.setAttribute('aria-expanded', !expanded)
		})

		if (expanded) {
			enableBodyScroll(homeContactMenu)
			focusTrapContactMenu.deactivate()
			collapseAllSubmenus()
		} else {
			disableBodyScroll(homeContactMenu)
			setTimeout(() => {
				focusTrapContactMenu.activate()
			}, 150)
		}
	} else if (e.keyCode === 27) {
		homeContactMenu.setAttribute('data-visible', false)

		homeContactButtons.forEach((item) => {
			item.setAttribute('aria-expanded', false)
		})
		enableBodyScroll(homeContactMenu)
		focusTrapContactMenu.deactivate()
		collapseAllSubmenus()
	}
}

/**
 * Handle project table sorting
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2023.1
 */
export function handleProjectSorting(e) {
	let table
	let rows
	let switching
	let i
	let x
	let y
	let shouldSwitch
	let dir
	const switchcount = 0

	const filter = e.currentTarget.getAttribute('data-sort-target')
	const currentdirection = e.currentTarget.getAttribute('data-sort-direction')

	// Calculate direction.
	dir = 'desc'
	if (currentdirection === 'desc') {
		dir = 'asc'
	}

	// Reset button directions.
	sortTableButtons.forEach((item) => {
		item.setAttribute('data-sort-direction', 'none')
	})

	e.currentTarget.setAttribute('data-sort-direction', dir)

	table = document.querySelector('.js-layout')
	switching = true
	// Set the sorting direction to ascending:
	/* Make a loop that will continue until
  no switching has been done: */
	while (switching) {
		// Start by saying: no switching is done:
		switching = false
		// rows = table.rows
		rows = table.querySelectorAll('article')
		/* Loop through all table rows (except the
    first, which contains table headers): */
		for (i = 0; i < rows.length - 1; i++) {
			// Start by saying there should be no switching:
			shouldSwitch = false
			/* Get the two elements you want to compare,
      one from current row and one from the next: */
			x = rows[i].querySelector(`[data-sort="${filter}"]`)
			y = rows[i + 1].querySelector(`[data-sort="${filter}"]`)
			/* Check if the two rows should switch place,
      based on the direction, asc or desc: */
			if (dir == 'asc') {
				if (
					x.innerHTML
						.toLowerCase()
						.localeCompare(y.innerHTML.toLowerCase(), 'es') > 0
				) {
					// If so, mark as a switch and break the loop:
					shouldSwitch = true
					break
				}
			} else if (dir == 'desc') {
				if (
					x.innerHTML
						.toLowerCase()
						.localeCompare(y.innerHTML.toLowerCase(), 'es') < 0
				) {
					// If so, mark as a switch and break the loop:
					shouldSwitch = true
					break
				}
			}
		}
		if (shouldSwitch) {
			/* If a switch has been marked, make the switch
      and mark that a switch has been done: */
			rows[i].parentNode.insertBefore(rows[i + 1], rows[i])
			switching = true
		}
	}
}

/**
 * Handle Media Text menu titles position
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2023.1
 */
export function moveBlockTitles(e) {
	if (mediaTextBlocks) {
		if (e.matches) {
			mediaTextBlocks.forEach((item) => {
				const heading = item.querySelector('h2')
				const content = item.querySelector('.wp-block-media-text__content')

				if (heading) {
					content.insertBefore(heading, content.firstChild)
				}
			})
		} else {
			mediaTextBlocks.forEach((item) => {
				const heading = item.querySelector('h2')

				if (heading) {
					item.insertBefore(heading, item.firstChild)
				}
			})
		}
	}
}

/**
 * Update page state
 *
 * @version     1.0.0
 * @since       2024.1.1
 */
function updatePageFilterState(newFilter) {
	const url = new URL(window.location)
	url.searchParams.set('filter', newFilter)
	window.history.replaceState({}, '', url)
	if (newFilter === 'all') {
		document.title = `Proyectos — DPYA Arquitectura`
	} else {
		const newFilterTitle = newFilter[0].toUpperCase() + newFilter.slice(1)
		document.title = `Proyectos — ${newFilterTitle} — DPYA Arquitectura`
	}
}
